import apiClient from './api'

export const sessionService = {
  /**
   * Get session by ID
   * @param {string} sessionId - The session ID to retrieve
   * @returns {Promise<Object>} Session data response
   */
  async getSession(sessionId) {
    const response = await apiClient.get(`/sessions/${sessionId}`)
    return response.data
  },

  /**
   * Get all sessions
   * @returns {Promise<Array>} Array of session objects
   */
  async getAllSessions() {
    const response = await apiClient.get('/sessions')
    return response.data
  },

 /** 
  * Get session state by ID
   * @param {string} sessionId - The session ID state to retrieve
   * @returns {Promise<Object>} Session state data response
   */
  async getSessionState(sessionId) {
    const response = await apiClient.get(`/sessions/${sessionId}/state`)
    return response.data
  },

  /**
   * Delete a session by ID
   * @param {string} sessionId - The session ID to delete
   * @returns {Promise<void>} Delete response
   */
  async deleteSession(sessionId) {
    await apiClient.delete(`/sessions/${sessionId}`)
  },

  /**
   * Rename a session by ID
   * @param {string} sessionId - The session ID to rename
   * @param {string} name - The new name for the session
   * @returns {Promise<Object|void>} Updated session or empty response
   */
  async renameSession(sessionId, name) {
    const response = await apiClient.put(`/sessions/${sessionId}`, { name })
    return response.data
  },

  /**
   * Create a new session with a name
   * @param {string} name - The name for the new session
   * @returns {Promise<Object>} Created session
   */
  async createSession(name) {
    const response = await apiClient.post('/sessions', { name })
    return response.data
  },

  /**
   * Update a brief section
   * @param {string} sessionId - The session ID
   * @param {Object} sectionData - The section data (briefSection2DTO)
   * @param {boolean} reSummarise - Whether to re-summarise the section
   * @returns {Promise<Object>} Update response
   */
  async updateBriefSection(sessionId, sectionData, reSummarise = true) {
    const response = await apiClient.put(`/sessions/${sessionId}/section`, {
      ...sectionData,
      reSummarise
    })
    return response.data
  },

  /**
   * Submit content for review (NEW - Content Reviewer)
   * @param {string} sessionId - The session ID
   * @param {string} content - The content to review
   * @param {boolean} rewriteMode - Whether to rewrite/improve content
   * @returns {Promise<Object>} Submit response
   */
  async submitContent(sessionId, content, rewriteMode = false) {
    const response = await apiClient.post(`/sessions/${sessionId}/content`, {
      content,
      rewriteMode
    })
    return response.data
  },

  /**
   * Get content review status (NEW - Content Reviewer)
   * @param {string} sessionId - The session ID
   * @returns {Promise<Object>} Content review data with 5 criteria
   */
  async getContentReview(sessionId) {
    const response = await apiClient.get(`/sessions/${sessionId}/content-review`)
    return response.data
  },

  /**
   * Get generic reviews (REPLACES criteria reviews)
   * Supports multiple review types: Brand TOV, SEO, Accessibility, etc.
   * @param {string} sessionId - The session ID
   * @returns {Promise<Object>} Reviews data with reviewsAllComplete flag
   */
  async getReviews(sessionId) {
    const response = await apiClient.get(`/sessions/${sessionId}/reviews`)
    return response.data
  },

  /**
   * Request translation for a section (NEW - Translation feature)
   * @param {string} sessionId - The session ID
   * @param {string} headingId - The section heading ID to translate
   * @param {string} language - The target language (french, italian, german)
   * @returns {Promise<Object>} Translation request response (fire and forget)
   */
  async requestTranslation(sessionId, headingId, language) {
    const response = await apiClient.post(`/sessions/${sessionId}/translate`, {
      headingId,
      language
    })
    return response.data
  },

  /**
   * Get all translations for a session (NEW - Translation feature)
   * @param {string} sessionId - The session ID
   * @returns {Promise<Object>} All translations with status and completion flag
   */
  async getTranslations(sessionId) {
    const response = await apiClient.get(`/sessions/${sessionId}/translations`)
    return response.data
  },

  /**
   * Delete a translation (NEW - Translation feature)
   * @param {string} sessionId - The session ID
   * @param {string} headingId - The section heading ID
   * @param {string} language - The language of the translation to delete
   * @returns {Promise<Object>} Delete response
   */
  async deleteTranslation(sessionId, headingId, language) {
    const response = await apiClient.delete(`/sessions/${sessionId}/translations/${headingId}/${language}`)
    return response.data
  },

  /**
   * Request persona-ification of content (NEW - Persona feature)
   * @param {string} sessionId - The session ID
   * @param {string} headingId - The heading ID of the content to persona-ify
   * @param {Array<string>} personaTypes - Array of persona types (1-4 items)
   * @returns {Promise<Object>} Persona request response
   */
  async requestPersona(sessionId, headingId, personaTypes) {
    const response = await apiClient.post(`/sessions/${sessionId}/persona`, {
      headingId,
      personaTypes
    })
    return response.data
  },

  /**
   * Delete a persona section (NEW - Persona feature)
   * @param {string} sessionId - The session ID
   * @param {string} headingId - The persona section heading ID to delete
   * @returns {Promise<Object>} Delete response
   */
  async deletePersonaSection(sessionId, headingId) {
    const response = await apiClient.delete(`/sessions/${sessionId}/sections/${headingId}`)
    return response.data
  },

  /**
   * Request review for a persona section (NEW - Persona feature)
   * @param {string} sessionId - The session ID
   * @param {string} headingId - The persona section heading ID to review
   * @returns {Promise<Object>} Review request response
   */
  async requestPersonaReview(sessionId, headingId) {
    const response = await apiClient.post(`/sessions/${sessionId}/sections/${headingId}/review`)
    return response.data
  },

  /**
   * Get all personifications for a session (NEW - Persona feature)
   * @param {string} sessionId - The session ID
   * @returns {Promise<Object>} Personifications data
   */
  async getPersonifications(sessionId) {
    const response = await apiClient.get(`/sessions/${sessionId}/personifications`)
    return response.data
  }

} 